import api from './api'

const apiEmail = {
    async getConversations(axios, organizationId, filters = {}, paging = {}) {
        const params = { ...filters, ...paging }

        Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null) delete params[key]
        })

        return axios
            .get('/conversations', { params })
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => {
                api.handleError(error)
                return api.createErrorResponse(error)
            })
    },

    async getConversation(axios, conversationId) {
        return axios
            .get(`/conversations/${conversationId}`)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async getConversationsByCreator(axios, creatorEmail) {
        return axios
            .get(`/conversations/by-creator/${encodeURIComponent(creatorEmail)}`)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => {
                api.handleError(error)
                return api.createErrorResponse(error)
            })
    },

    async getConversationStats(axios) {
        return axios
            .get('/conversations/stats')
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async getMessagesForConversation(axios, conversationId, paging = {}) {
        const params = {
            page: paging.currentPage || 1,
            perPage: paging.perPage || 50,
            sortBy: paging.sortby || 'sent_at',
            sortDir: paging.sortdir || 'asc',
        }

        return axios
            .get(`/conversations/${conversationId}/messages`, { params })
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async archiveConversation(axios, conversationId) {
        return axios
            .put(`/conversations/${conversationId}`, { status: 'archived' })
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async unarchiveConversation(axios, conversationId) {
        return axios
            .put(`/conversations/${conversationId}`, { status: 'active' })
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async markConversationAsRead(axios, conversationId) {
        return axios
            .put(`/conversations/${conversationId}/read`)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async deleteConversation(axios, conversationId) {
        return axios
            .delete(`/conversations/${conversationId}`)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async bulkUpdateConversations(axios, conversationIds, action) {
        return axios
            .post('/conversations/bulk', {
                conversation_ids: conversationIds,
                action,
            })
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async linkConversationToCampaign(axios, conversationId, campaignId, participantId = null) {
        const body = { campaignId, ...(participantId && { participantId }) }
        return axios
            .post(`/conversations/${conversationId}/link-campaign`, body)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async sendEmail(axios, emailData, conversationId = null) {
        const endpoint = conversationId ? `/conversations/${conversationId}/messages` : '/conversations/messages' // new conversation
        return axios
            .post(endpoint, emailData)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async sendBulkEmail(axios, emailData) {
        return axios
            .post('/conversations/messages/bulk', emailData)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async replyToMessage(axios, messageId, replyData) {
        return axios
            .post(`/messages/${messageId}/reply`, replyData)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async markMessageAsRead(axios, messageId) {
        return axios
            .put(`/messages/${messageId}/read`)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async updateMessageMetadata(axios, messageId, metadata) {
        return axios
            .put(`/messages/${messageId}`, { metadata })
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => api.createErrorResponse(error))
    },

    async getConnectedAccounts(axios) {
        return axios
            .get('/email-accounts')
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => {
                api.handleError(error)
                return api.createErrorResponse(error)
            })
    },

    async downloadAttachment(axios, attachmentId, messageId) {
        return axios
            .get(`/attachments/${attachmentId}/download`, {
                params: { message_id: messageId },
                responseType: 'blob',
            })
            .then((response) => api.createSuccessResponse(response.data))
            .catch((error) => api.createErrorResponse(error))
    },

    async getEmailAccounts(axios) {
        return axios
            .get('/email-accounts')
            .then(({ data }) => api.createSuccessResponse(data.payload.accounts))
            .catch((error) => {
                api.handleError(error)
                return api.createErrorResponse(error)
            })
    },

    async connectEmailAccount(axios, provider = 'gmail') {
        return axios
            .post('/email-accounts/connect', { provider })
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => {
                api.handleError(error)
                return api.createErrorResponse(error)
            })
    },

    async disconnectEmailAccount(axios, accountId) {
        return axios
            .delete(`/email-accounts/${accountId}/disconnect`)
            .then(({ data }) => api.createSuccessResponse(data.payload))
            .catch((error) => {
                api.handleError(error)
                return api.createErrorResponse(error)
            })
    },
}

export default apiEmail
